import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Send, LogOut, CheckCircle2, Clock, AlertTriangle, ArrowLeft } from 'lucide-react';
import api from '../services/api';
import { useAuthStore } from '../store/useStore';
import '../styles/Acquisizione.css';

/**
 * Acquisizione — il telefono dello studio fotografa un documento e lo
 * mette in coda. Fine.
 *
 * LA REGOLA CHE DECIDE TUTTO: l'acquisizione e' separata dalla
 * conferma. Da qui non si legge un paziente, non si vede una fattura,
 * non si carica niente in magazzino. La conferma resta nella schermata
 * di controllo del magazzino, aperta da una persona con la sua sessione,
 * che guarda le righe e le carica: `registrato_da` e' chi conferma.
 *
 * Pensata per un telefono: un pulsante che apre la fotocamera
 * posteriore, la scelta fra bolla e fattura, invia. Sotto, i propri
 * invii con nome, ora e stato - e basta. Non ha il menu del gestionale:
 * chi ha in mano il telefono non deve trovarci altro.
 */

const STATO = {
    in_coda: { testo: 'in lettura', Icona: Clock, classe: 'attesa' },
    letta:   { testo: 'letta, da confermare al banco', Icona: CheckCircle2, classe: 'ok' },
    fallita: { testo: 'non leggibile: rifai la foto', Icona: AlertTriangle, classe: 'ko' },
};

const ora = (v) => new Date(v).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' });

const Acquisizione = () => {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const logout = useAuthStore((s) => s.logout);
    const [tipo, setTipo] = useState('bolla');
    const [file, setFile] = useState(null);
    const [anteprima, setAnteprima] = useState(null);
    const [invio, setInvio] = useState(false);
    const [esito, setEsito] = useState(null);
    const [errore, setErrore] = useState(null);
    const [invii, setInvii] = useState([]);

    const carica = useCallback(async () => {
        try {
            const r = await api.get('/acquisizioni/mie');
            setInvii(r.data.data.invii || []);
        } catch (e) { /* l'elenco resta com'era */ }
    }, []);

    useEffect(() => { carica(); }, [carica]);

    /* L'elenco si aggiorna da solo finche' c'e' qualcosa in lettura:
       chi ha appena scattato vuole vedere «letta» senza toccare niente. */
    useEffect(() => {
        if (!invii.some((x) => x.stato === 'in_coda')) return undefined;
        const t = setInterval(carica, 3000);
        return () => clearInterval(t);
    }, [invii, carica]);

    const scegli = (f) => {
        setEsito(null); setErrore(null);
        setFile(f || null);
        if (anteprima) URL.revokeObjectURL(anteprima);
        setAnteprima(f && f.type.startsWith('image/') ? URL.createObjectURL(f) : null);
    };

    const invia = async () => {
        if (!file) return;
        try {
            setInvio(true); setErrore(null);
            const fd = new FormData();
            fd.append('file', file);
            fd.append('tipo', tipo);
            const r = await api.post('/acquisizioni', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setEsito(r.data.data.gia_letta
                ? 'Questa foto era già stata inviata.'
                : `${tipo === 'bolla' ? 'Bolla' : 'Fattura'} in coda. Il carico lo conferma il banco.`);
            scegli(null);
            await carica();
        } catch (e) {
            setErrore(e?.message || 'Invio non riuscito. Riprova.');
        } finally {
            setInvio(false);
        }
    };

    const esci = () => { logout(); navigate('/login', { replace: true }); };

    return (
        <div className="acq-wrap">
            <header className="acq-testata">
                {user?.ruolo !== 'acquisizione' && (
                    <button className="acq-icona" onClick={() => navigate('/magazzino')} title="Torna al magazzino">
                        <ArrowLeft size={20} />
                    </button>
                )}
                <b>Fotografa un documento</b>
                <button className="acq-icona" onClick={esci} title="Esci"><LogOut size={20} /></button>
            </header>

            <div className="acq-tipo">
                <button className={tipo === 'bolla' ? 'attivo' : ''} onClick={() => setTipo('bolla')}>Bolla</button>
                <button className={tipo === 'fattura' ? 'attivo' : ''} onClick={() => setTipo('fattura')}>Fattura</button>
            </div>
            <p className="acq-nota">
                {tipo === 'bolla'
                    ? 'Dalla bolla si leggono articoli, quantità, lotto e scadenza.'
                    : 'Dalla fattura si leggono articoli, quantità e prezzi.'}
                {' '}Inquadra tutto il foglio, senza ombre.
            </p>

            {/* `capture="environment"` apre direttamente la fotocamera
                posteriore sui telefoni; su un computer diventa un
                normale selettore di file. */}
            <label className="acq-scatta">
                <Camera size={28} />
                <span>{file ? 'Rifai la foto' : 'Apri la fotocamera'}</span>
                <input type="file" accept="image/*,application/pdf" capture="environment"
                       onChange={(e) => scegli(e.target.files[0])} />
            </label>

            {anteprima && <img className="acq-anteprima" src={anteprima} alt="anteprima del documento" />}
            {file && !anteprima && <p className="acq-nota">{file.name}</p>}

            <button className="acq-invia" disabled={!file || invio} onClick={invia}>
                <Send size={18} /> {invio ? 'Invio…' : `Invia la ${tipo}`}
            </button>

            {esito && <div className="acq-esito ok">{esito}</div>}
            {errore && <div className="acq-esito ko">{errore}</div>}

            <h2>I tuoi invii</h2>
            {invii.length === 0 ? (
                <p className="acq-nota">Nessun invio negli ultimi sette giorni.</p>
            ) : (
                <ul className="acq-elenco">
                    {invii.map((x) => {
                        const s = x.caricata
                            ? { testo: 'caricata in magazzino', Icona: CheckCircle2, classe: 'ok' }
                            : (STATO[x.stato] || STATO.in_coda);
                        return (
                            <li key={x.id} className={s.classe}>
                                <s.Icona size={18} />
                                <span>
                                    <b>{x.tipo === 'bolla' ? 'Bolla' : 'Fattura'}</b> · {ora(x.created_at)}
                                    <em>{s.testo}</em>
                                </span>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default Acquisizione;
